import { useCallback, useEffect, useRef, useState } from "react";

interface ImageLightboxProps {
  src: string | null;
  alt?: string;
  onClose: () => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_CLICK_ZOOM = 2.5;
const WHEEL_SENSITIVITY = 0.0015;

const clamp = (n: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, n));

const touchDistance = (touches: React.TouchList | TouchList) => {
  const [a, b] = [touches[0], touches[1]];
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy);
};

const touchMidpoint = (touches: React.TouchList | TouchList) => {
  const [a, b] = [touches[0], touches[1]];
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
};

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  src,
  alt = "Preview",
  onClose,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  // Mouse drag pan state
  const dragRef = useRef<{ startX: number; startY: number; moved: boolean } | null>(null);

  // Two-finger pinch state
  const pinchRef = useRef<{ startDistance: number; startScale: number } | null>(
    null
  );

  const resetZoom = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  // Reset zoom/pan whenever the displayed image changes — otherwise the
  // previous image's pan stays applied to the next one.
  useEffect(() => {
    resetZoom();
  }, [src, resetZoom]);

  // Close on Escape, and lock body scroll while the overlay is open so
  // the page underneath doesn't drift behind a centered image.
  useEffect(() => {
    if (!src) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [src, onClose]);

  // Native wheel + touchmove listeners with `{ passive: false }` — React's
  // synthetic events register as passive by default in modern browsers,
  // so `preventDefault()` inside `onWheel`/`onTouchMove` is silently
  // dropped (page scrolls / pinches the whole viewport instead of the
  // image).
  useEffect(() => {
    if (!src) return;
    const el = imgRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      setScale((prev) => {
        const next = clamp(prev * (1 - e.deltaY * WHEEL_SENSITIVITY), MIN_SCALE, MAX_SCALE);
        // Keep the point under the cursor stationary across the zoom.
        const k = next / prev;
        setTranslate((t) => ({
          x: t.x - (cx - t.x) * (k - 1),
          y: t.y - (cy - t.y) * (k - 1),
        }));
        // Snap pan back to origin when we hit min scale so the image
        // doesn't stay offset after the user fully zooms out.
        if (next === MIN_SCALE) setTranslate({ x: 0, y: 0 });
        return next;
      });
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault();
        const dist = touchDistance(e.touches);
        const nextScale = clamp(
          pinchRef.current.startScale * (dist / pinchRef.current.startDistance),
          MIN_SCALE,
          MAX_SCALE
        );
        setScale(nextScale);
        if (nextScale === MIN_SCALE) setTranslate({ x: 0, y: 0 });
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchmove", onTouchMove);
    };
  }, [src]);

  if (!src) return null;

  const onMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    if (scale <= MIN_SCALE) return;
    dragRef.current = {
      startX: e.clientX - translate.x,
      startY: e.clientY - translate.y,
      moved: false,
    };
  };

  const onMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!dragRef.current) return;
    const nx = e.clientX - dragRef.current.startX;
    const ny = e.clientY - dragRef.current.startY;
    dragRef.current.moved = true;
    setTranslate({ x: nx, y: ny });
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  const onTouchStart = (e: React.TouchEvent<HTMLImageElement>) => {
    if (e.touches.length === 2) {
      pinchRef.current = {
        startDistance: touchDistance(e.touches),
        startScale: scale,
      };
    } else if (e.touches.length === 1 && scale > MIN_SCALE) {
      const t = e.touches[0];
      dragRef.current = {
        startX: t.clientX - translate.x,
        startY: t.clientY - translate.y,
        moved: false,
      };
    }
  };

  // Single-finger pan when zoomed. Pinch handler is attached as a native
  // listener (above) so it can call preventDefault.
  const onTouchMoveReact = (e: React.TouchEvent<HTMLImageElement>) => {
    if (e.touches.length === 1 && dragRef.current) {
      const t = e.touches[0];
      const nx = t.clientX - dragRef.current.startX;
      const ny = t.clientY - dragRef.current.startY;
      dragRef.current.moved = true;
      setTranslate({ x: nx, y: ny });
    }
  };

  const onTouchEnd = (e: React.TouchEvent<HTMLImageElement>) => {
    if (e.touches.length < 2) pinchRef.current = null;
    if (e.touches.length === 0) dragRef.current = null;
  };

  const onDoubleClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (scale > MIN_SCALE) {
      resetZoom();
      return;
    }
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left - rect.width / 2;
    const cy = e.clientY - rect.top - rect.height / 2;
    const k = DOUBLE_CLICK_ZOOM;
    setScale(k);
    setTranslate({ x: -cx * (k - 1), y: -cy * (k - 1) });
  };

  // Suppress backdrop click-to-close when the user just finished a drag.
  // Without this, releasing the mouse over the dark area after dragging
  // a zoomed image dismisses the lightbox unexpectedly.
  const onImgClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation();
  };

  const onBackdropClick = () => {
    if (dragRef.current?.moved) return;
    onClose();
  };

  const zoomAtCenter = (target: number) => {
    setScale(target);
    if (target === MIN_SCALE) setTranslate({ x: 0, y: 0 });
  };

  const cursor =
    scale > MIN_SCALE
      ? dragRef.current
        ? "grabbing"
        : "grab"
      : "zoom-in";

  return (
    <div
      className="imageLightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      onClick={onBackdropClick}
    >
      <button
        type="button"
        className="imageLightbox__close"
        aria-label="Close preview"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <i className="bi bi-x-lg" aria-hidden="true" />
      </button>

      <div
        className="imageLightbox__controls"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="imageLightbox__ctrl"
          aria-label="Zoom out"
          onClick={() => zoomAtCenter(clamp(scale - 0.5, MIN_SCALE, MAX_SCALE))}
          disabled={scale <= MIN_SCALE}
        >
          <i className="bi bi-zoom-out" aria-hidden="true" />
        </button>
        <span className="imageLightbox__scale">{Math.round(scale * 100)}%</span>
        <button
          type="button"
          className="imageLightbox__ctrl"
          aria-label="Zoom in"
          onClick={() => zoomAtCenter(clamp(scale + 0.5, MIN_SCALE, MAX_SCALE))}
          disabled={scale >= MAX_SCALE}
        >
          <i className="bi bi-zoom-in" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="imageLightbox__ctrl"
          aria-label="Reset zoom"
          onClick={resetZoom}
          disabled={scale === MIN_SCALE && translate.x === 0 && translate.y === 0}
        >
          <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
        </button>
      </div>

      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className="imageLightbox__img"
        draggable={false}
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transition: dragRef.current ? "none" : "transform 0.1s ease-out",
          cursor,
          touchAction: "none",
        }}
        onClick={onImgClick}
        onDoubleClick={onDoubleClick}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMoveReact}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      />
    </div>
  );
};

export default ImageLightbox;
